import type { ReactNode } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import { Dialog, DialogContent, DialogTrigger } from "@b2bportal/core-ui";
import { useI18nContext } from "@hopper-b2b/i18n";
import clsx from "clsx";
import DOMPurify from "dompurify";
import { ApacUiComponent } from "../../context";
import { useApacUiStyles } from "../../hooks";
import defaultStyles from "./NoticeBanner.module.scss";

export enum NoticePriority {
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low",
}

export type NoticeBannerProps = {
  priority?: NoticePriority;
  popupClassName?: string;
  bannerClassName?: string;
  title: string;
  description: string;
  ctaText?: ReactNode;
};

export const NoticeBanner = (props: NoticeBannerProps) => {
  const {
    priority = NoticePriority.MEDIUM,
    popupClassName,
    bannerClassName,
    title,
    description,
    ctaText,
  } = props;

  const { t } = useI18nContext();
  const styles = useApacUiStyles(ApacUiComponent.NoticeBanner, defaultStyles);
  const [block, cn] = useModuleBEM(styles, "NoticeBanner");

  return (
    <Dialog>
      <DialogTrigger
        className={clsx(bannerClassName, cn("banner", [priority]))}
      >
        <span className={cn("title", [priority])}>{title}</span>
        <span>{ctaText ?? t("apac.noticeBanner.buttonCaption")}</span>
      </DialogTrigger>
      <DialogContent
        title={title}
        className={clsx(block, popupClassName, cn("content", [priority]))}
      >
        <div
          className={cn("description")}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(description, {
              USE_PROFILES: { html: true },
              ADD_ATTR: ["target"],
            }),
          }}
        />
      </DialogContent>
    </Dialog>
  );
};
